// Serviço para consumir, em tempo de execução, as chaves de API públicas
// (não-sensíveis) configuradas na tela "Configuração" do Gerenciador de
// Produtos (Google OAuth Client ID, chaves publicáveis de pagamento,
// status do Melhor Envio e dados bancários para transferência/boleto).
//
// O e-commerce NUNCA deve guardar segredos (secret keys / access tokens)
// no front-end - esses continuam apenas no Gerenciador de Produtos e são
// usados pelo backend/servidor quando necessário.

import { gerenciadorUrl } from "@/config/gerenciadorConfig";

let cachedConfigPromise = null;

/**
 * Busca (com cache em memória durante a sessão da página) as configurações
 * públicas do Gerenciador de Produtos.
 *
 * Em produção, a chamada é feita diretamente (cross-domain) para o
 * config_api.php do Gerenciador, cuja URL é resolvida de forma
 * centralizada em @/config/gerenciadorConfig.js - o mesmo padrão
 * comprovado já usado por getBanners()/getDestaquesSite() e pelas
 * chamadas de categorias/subcategorias (dados_api.php) em api.js e
 * CategoriasSite.vue. Esse método é mais confiável porque não depende de
 * "adivinhar" a estrutura de pastas do cPanel (../../.. a partir de
 * public_html/api) para localizar o arquivo pelo sistema de arquivos do
 * servidor - o endpoint já libera CORS (Access-Control-Allow-Origin: *)
 * para scope=public.
 *
 * Em desenvolvimento local, mantém o proxy same-origin /api/config_api.php
 * como fallback.
 */
export function getPublicApiConfig() {
  if (cachedConfigPromise) {
    return cachedConfigPromise;
  }

  const proxyUrl = "/api/config_api.php";
  const isLocal =
    process.env.VUE_APP_ENV === "dev" ||
    window.location.hostname.includes("localhost");
  const directUrl = `${gerenciadorUrl()}/api/config_api.php?scope=public`;

  // Em produção tenta primeiro a chamada direta ao Gerenciador e, se ela
  // falhar (rede/CORS/resposta inválida), usa o proxy same-origin do
  // próprio e-commerce (api/config_api.php) como segunda tentativa.
  const urls = isLocal ? [proxyUrl] : [directUrl, proxyUrl];

  const tentar = (i) =>
    fetch(urls[i], { cache: "no-store" })
      .then((res) => res.json())
      .then((json) => {
        if (json && json.status === "ok") {
          return json.data || {};
        }
        throw new Error("Resposta inválida de " + urls[i]);
      })
      .catch((err) => {
        if (i + 1 < urls.length) {
          return tentar(i + 1);
        }
        throw err;
      });

  cachedConfigPromise = tentar(0).catch(() => {
    // Falha total: não mantém o erro em cache (permite nova tentativa na
    // próxima chamada) e devolve objeto vazio para os componentes usarem
    // seus valores padrão sem quebrar a experiência do usuário.
    cachedConfigPromise = null;
    return {};
  });

  return cachedConfigPromise;
}

export default { getPublicApiConfig };
